import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  FileText, 
  CheckCircle, 
  Clock, 
  User,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Shield,
  Users,
  BookOpen
} from 'lucide-react';
import Header from '../common/Header';

const StaffDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleDisplayName = (userType) => {
    switch (userType) {
      case 'class_teacher': return 'Class Teacher';
      case 'warden': return 'Warden';
      case 'principal': return 'Principal';
      case 'security': return 'Security Staff';
      default: return 'Staff Member';
    }
  };

  const getRoleColor = (userType) => {
    switch (userType) {
      case 'class_teacher': return 'bg-blue-100 text-blue-800';
      case 'warden': return 'bg-indigo-100 text-indigo-800';
      case 'principal': return 'bg-purple-100 text-purple-800';
      case 'security': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100">
      <Header title="Staff Dashboard" showBack={false} />
      
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-4">
            <div className="bg-teal-100 rounded-full p-3">
              <Shield className="h-8 w-8 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome back, {user?.firstName || 'Staff Member'}!
              </h1>
              <p className="text-gray-600">
                Manage student outings and administrative tasks
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(user?.userType)}`}>
              {getRoleDisplayName(user?.userType)}
            </span>
          </div>
          
          {/* User Info */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <User className="h-4 w-4" />
              <span>{user?.firstName} {user?.lastName}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <Phone className="h-4 w-4" />
              <span>{user?.mobile}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <Mail className="h-4 w-4" />
              <span>{user?.email}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <Shield className="h-4 w-4" />
              <span>{getRoleDisplayName(user?.userType)}</span>
            </div>
          </div>
        </div>

        {/* Staff Management Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-blue-100 rounded-lg p-2">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Staff Management</h2>
          </div>
          
          {/* Outing Management Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Outing Management</h2>
                <p className="text-gray-600">Review and approve student outing requests</p>
              </div>
              <button
                onClick={() => navigate('/outing/staff')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Manage Outings
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center space-x-3 mb-3">
                  <Calendar className="h-6 w-6 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Pending Requests</h3>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  Review and approve pending outing requests from students
                </p>
                <button
                  onClick={() => navigate('/outing/staff')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  View Requests →
                </button>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center space-x-3 mb-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <h3 className="font-semibold text-green-900">Approved Outings</h3>
                </div>
                <p className="text-sm text-green-700 mb-3">
                  Track approved outings and student movement
                </p>
                <button
                  onClick={() => navigate('/outing/staff')}
                  className="text-green-600 hover:text-green-700 text-sm font-medium"
                >
                  View Status →
                </button>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-3 mb-3">
                  <MapPin className="h-6 w-6 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">History</h3>
                </div>
                <p className="text-sm text-purple-700 mb-3">
                  View complete outing history and analytics
                </p>
                <button
                  onClick={() => navigate('/outing/staff')}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  View History →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-purple-100 rounded-lg p-2">
              <BookOpen className="h-6 w-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Quick Actions</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Student Records - Placeholder */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60">
              <div className="flex items-center space-x-3 mb-3">
                <FileText className="h-6 w-6 text-gray-400" />
                <h3 className="font-semibold text-gray-500">Student Records</h3>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                Access student academic and attendance records
              </p>
              <div className="text-gray-400 text-sm font-medium">Coming Soon</div>
            </div>
            
            {/* Campus Management - Placeholder */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60">
              <div className="flex items-center space-x-3 mb-3">
                <MapPin className="h-6 w-6 text-gray-400" />
                <h3 className="font-semibold text-gray-500">Campus Management</h3>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                Manage campus settings and configurations
              </p>
              <div className="text-gray-400 text-sm font-medium">Coming Soon</div>
            </div>
            
            {/* Reports - Placeholder */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60">
              <div className="flex items-center space-x-3 mb-3">
                <GraduationCap className="h-6 w-6 text-gray-400" />
                <h3 className="font-semibold text-gray-500">Reports</h3>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                Generate and view various system reports
              </p>
              <div className="text-gray-400 text-sm font-medium">Coming Soon</div>
            </div>
          </div>
        </div>

        {/* Staff Notices Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Staff Notices & Updates</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Outing Approval Deadline</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Please review and respond to outing requests within 24 hours.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-900">Campus Rules Update</h3>
                <p className="text-sm text-green-700 mt-1">
                  New campus rules have been implemented. Please review the updated guidelines.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-900">Staff Meeting</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Monthly staff meeting scheduled for next Friday at 3:00 PM.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
